import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const MarketplaceChannelForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    marketplace_type: 'amazon',
    integration_id: '',
    auto_publish: false,
    auto_sync: true,
    sync_frequency: 3600,
    credentials: {},
    config: {}
  });

  const isEditing = Boolean(id);

  const marketplaceTypes = [
    { value: 'amazon', label: 'Amazon' },
    { value: 'aliexpress', label: 'AliExpress' },
    { value: 'temu', label: 'Temu' },
    { value: 'shein', label: 'Shein' },
    { value: 'magento', label: 'Magento 2' },
    { value: 'woocommerce', label: 'WooCommerce' },
    { value: 'tiktok', label: 'TikTok Shop' },
    { value: 'instagram', label: 'Instagram Shopping' },
    { value: 'facebook', label: 'Facebook Shopping' }
  ];

  const credentialFields = {
    amazon: [
      { key: 'seller_id', label: 'Seller ID', type: 'text' },
      { key: 'access_key', label: 'Access Key', type: 'text' },
      { key: 'secret_key', label: 'Secret Key', type: 'password' },
      { key: 'marketplace_id', label: 'Marketplace ID', type: 'text' },
      { key: 'region', label: 'Region', type: 'text', placeholder: 'e.g., us-east-1' }
    ],
    aliexpress: [
      { key: 'app_key', label: 'App Key', type: 'text' },
      { key: 'app_secret', label: 'App Secret', type: 'password' }
    ],
    temu: [
      { key: 'api_key', label: 'API Key', type: 'password' },
      { key: 'shop_id', label: 'Shop ID', type: 'text' }
    ],
    shein: [
      { key: 'api_key', label: 'API Key', type: 'password' },
      { key: 'merchant_id', label: 'Merchant ID', type: 'text' }
    ],
    magento: [
      { key: 'base_url', label: 'Base URL', type: 'text', placeholder: 'https://your-store.com' },
      { key: 'access_token', label: 'Access Token', type: 'password' },
      { key: 'store_code', label: 'Store Code', type: 'text' }
    ],
    woocommerce: [
      { key: 'base_url', label: 'Store URL', type: 'text', placeholder: 'https://your-store.com' },
      { key: 'consumer_key', label: 'Consumer Key', type: 'text' },
      { key: 'consumer_secret', label: 'Consumer Secret', type: 'password' }
    ],
    tiktok: [
      { key: 'app_key', label: 'App Key', type: 'text' },
      { key: 'app_secret', label: 'App Secret', type: 'password' },
      { key: 'shop_id', label: 'Shop ID', type: 'text' }
    ],
    instagram: [
      { key: 'access_token', label: 'Access Token', type: 'password' },
      { key: 'business_account_id', label: 'Business Account ID', type: 'text' },
      { key: 'catalog_id', label: 'Catalog ID', type: 'text', placeholder: 'Optional' }
    ],
    facebook: [
      { key: 'access_token', label: 'Access Token', type: 'password' },
      { key: 'page_id', label: 'Page ID', type: 'text' },
      { key: 'catalog_id', label: 'Catalog ID', type: 'text' }
    ]
  };

  useEffect(() => {
    loadIntegrations();
    if (id) {
      fetchChannel();
    }
  }, [id]);

  const loadIntegrations = async () => {
    try {
      const response = await api.get('/integrations');
      // Filter for marketplace integrations
      const marketplaceIntegrations = response.data.integrations.filter(
        i => ['amazon', 'aliexpress', 'temu', 'shein', 'magento', 'woocommerce', 'tiktok', 'instagram', 'facebook'].includes(i.platform)
      );
      setIntegrations(marketplaceIntegrations);
    } catch (err) {
      console.error('Failed to load integrations:', err);
    }
  };

  const fetchChannel = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/marketplace/channels/${id}`);
      const channel = response.data;
      setFormData({
        name: channel.name,
        marketplace_type: channel.marketplace_type,
        integration_id: channel.integration_id,
        auto_publish: channel.auto_publish,
        auto_sync: channel.auto_sync,
        sync_frequency: channel.sync_frequency,
        credentials: channel.credentials || {},
        config: channel.config || {}
      });
    } catch (err) {
      setError('Failed to load channel');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await api.put(`/marketplace/channels/${id}`, formData);
      } else {
        await api.post('/marketplace/channels', formData);
      }
      navigate('/marketplace/channels');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save channel');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCredentialChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [key]: value
      }
    }));
  };

  const fields = credentialFields[formData.marketplace_type] || [];

  if (loading && isEditing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/marketplace/channels')}
          className="breadcrumb-link"
        >
          Marketplace Channels
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {isEditing ? 'Edit Channel' : 'New Channel'}
        </span>
      </nav>

      {/* Form Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              {isEditing ? 'Edit Marketplace Channel' : 'Create New Marketplace Channel'}
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              {isEditing ? 'Update channel configuration' : 'Configure a new marketplace sales channel'}
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'flex-end',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid var(--gray-200)'
        }}>
          <button
            type="button"
            onClick={() => navigate('/marketplace/channels')}
            disabled={loading}
            className="btn btn-outline"
          >
            ✕ Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
            form="channel-form"
          >
            {loading ? 'Saving...' : (isEditing ? '💾 Update' : 'Create Channel')}
          </button>
        </div>

        <form id="channel-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Channel Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., US Amazon Store"
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Marketplace Type *
              </label>
              <select
                name="marketplace_type"
                value={formData.marketplace_type}
                onChange={handleInputChange}
                required
              >
                {marketplaceTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontWeight: '600', 
              color: 'var(--gray-700)',
              fontSize: '0.875rem'
            }}>
              Integration *
            </label>
            <select
              name="integration_id"
              value={formData.integration_id}
              onChange={handleInputChange}
              required
            >
              <option value="">Select an integration...</option>
              {integrations.map(integration => (
                <option key={integration.id} value={integration.id}>
                  {integration.display_name} ({integration.platform})
                </option>
              ))}
            </select>
            {integrations.length === 0 && (
              <p style={{ fontSize: '0.85rem', color: '#dc3545', marginTop: '0.5rem' }}>
                No marketplace integrations found. Please add one in the <a href="/integrations">Integrations page</a>.
              </p>
            )}
          </div>

          <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#333' }}>Credentials</h3>
          
          {fields.map(field => (
            <div key={field.key} style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                {field.label}
              </label>
              <input
                type={field.type}
                value={formData.credentials[field.key] || ''}
                onChange={(e) => handleCredentialChange(field.key, e.target.value)}
                placeholder={field.placeholder || ''}
              />
            </div>
          ))}

          <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#333' }}>Sync Settings</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  name="auto_publish"
                  checked={formData.auto_publish}
                  onChange={handleInputChange}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Auto Publish
                </span>
              </label>
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                Automatically publish new products to this channel
              </p>
            </div>

            <div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  name="auto_sync"
                  checked={formData.auto_sync}
                  onChange={handleInputChange}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Auto Sync
                </span>
              </label>
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                Automatically sync inventory and prices
              </p>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Sync Frequency (seconds)
              </label>
              <input
                type="number"
                name="sync_frequency"
                value={formData.sync_frequency}
                onChange={handleInputChange}
                min="60"
                step="60"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarketplaceChannelForm;
